import { useState, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Typography,
  Tooltip,
  Flex,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  DollarOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  useServiceCharges,
  useCreateServiceCharge,
  useUpdateServiceCharge,
  useDeleteServiceCharge,
  useTodaySummary,
  useUsers,
  useServiceBonuses,
} from "../hooks/useServiceCharge";
import type { ServiceCharge } from "../services/serviceChargeService";
import ServiceChargeForm from "../components/serviceCharge/ServiceChargeForm";
import { useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;
const { Title } = Typography;

function ServiceChargePage() {
  const navigate = useNavigate();

  // ==================== STATE ====================
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    userId: undefined as string | undefined,
    serviceId: undefined as string | undefined,
    type: undefined as "service" | "recommend" | undefined,
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
    search: undefined as string | undefined,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ServiceCharge | null>(null);

  // ==================== QUERIES ====================
  const { data: chargesData, isLoading } = useServiceCharges(params);
  const { data: todaySummary } = useTodaySummary();
  const { data: userData, isLoading: userLoading } = useUsers({ limit: 100 });
  const { data: serviceBonusData, isLoading: serviceLoading } = useServiceBonuses({
    limit: 100,
    status: "active",
  });

  const createMutation = useCreateServiceCharge();
  const updateMutation = useUpdateServiceCharge();
  const deleteMutation = useDeleteServiceCharge();

  // ✅ ปรับปรุง - ใช้ nullish coalescing และ Array.isArray
  const users = Array.isArray(userData?.data?.employees) 
    ? userData.data.employees 
    : [];
    
  const services = Array.isArray(serviceBonusData?.data?.serviceBonuses)
    ? serviceBonusData.data.serviceBonuses
    : [];

  // ✅ สร้าง options ด้วย useMemo
  const userOptions = useMemo(() => {
    return [
      { label: "ທັງໝົດ", value: undefined },
      ...users.map((user: any) => ({
        label: user.name,
        value: user._id,
      })),
    ];
  }, [users]);



  // ==================== HANDLERS ====================
  const handleCreate = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record: ServiceCharge) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingRecord) {
        await updateMutation.mutateAsync({
          id: editingRecord._id,
          data: values,
        });
      } else {
        await createMutation.mutateAsync(values);
      }
      setModalOpen(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setParams({
        ...params,
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
        page: 1,
      });
    } else {
      setParams({
        ...params,
        startDate: undefined,
        endDate: undefined,
        page: 1,
      });
    }
  };

  const handleReset = () => {
    setParams({
      page: 1,
      limit: 10,
      userId: undefined,
      serviceId: undefined,
      type: undefined,
      startDate: undefined,
      endDate: undefined,
      search: undefined,
    });
  };

  // ==================== TABLE COLUMNS ====================
  const columns: ColumnsType<ServiceCharge> = [
    {
      title: "ລຳດັບ",
      key: "index",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_, __, index) => {
        return (params.page - 1) * params.limit + index + 1;
      },
    },
    {
      title: "ວັນທີ່",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "ພະນັກງານ",
      dataIndex: ["userId", "name"],
      key: "userName",
      width: 150,
    },
    {
      title: "ບໍລິການ",
      dataIndex: ["serviceId", "name"],
      key: "serviceName",
      width: 150,
    },
    {
      title: "ປະເພດ",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type: string) => (
        <Tag color={type === "service" ? "blue" : "green"}>
          {type === "service" ? "ບໍລິການ" : "ແນະນຳ"}
        </Tag>
      ),
    },
    {
      title: "ລາຄາ (ກີບ)",
      dataIndex: "price",
      key: "price",
      width: 120,
      align: "right",
      render: (price: number) => price?.toLocaleString() || "0",
    },
    {
      title: "ໝາຍເຫດ",
      dataIndex: "note",
      key: "note",
      ellipsis: true,
      render: (note: string) => note || "-",
    },
    {
      title: "ຜູ້ບັນທຶກ",
      dataIndex: ["createdBy", "name"],
      key: "createdBy",
      width: 120,
    },
    {
      title: "ການກະທຳ",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="ແກ້ໄຂ">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="ລຶບຂໍ້ມູນ"
            description="ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຂໍ້ມູນນີ້?"
            onConfirm={() => handleDelete(record._id)}
            okText="ລຶບ"
            cancelText="ຍົກເລີກ"
            okButtonProps={{
              loading: deleteMutation.isLoading,
              danger: true,
            }}
          >
            <Tooltip title="ລຶບ">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                loading={deleteMutation.isLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ==================== RENDER ====================
  return (
    <div>
      <Flex justify="space-between" align="center">
        <Title level={2}>ຈັດການຄ່າບໍລິການ</Title>
        <Button
          icon={<DashboardOutlined />}
          type="primary"
          style={{ backgroundColor: "#52c41a" }}
          onClick={() => navigate("/service-charge-report")}
        >
          ເບິ່ງລາຍງານ
        </Button>
      </Flex>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="ລາຍຮັບວັນນີ້"
              value={todaySummary?.data?.totals?.all || 0}
              precision={0}
              valueStyle={{ color: "#3f8600" }}
              prefix={<DollarOutlined />}
              suffix="ກີບ"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="ຄ່າບໍລິການ"
              value={todaySummary?.data?.totals?.service || 0}
              precision={0}
              valueStyle={{ color: "#1890ff" }}
              prefix={<DollarOutlined />}
              suffix="ກີບ"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="ຄ່າແນະນຳ"
              value={todaySummary?.data?.totals?.recommend || 0}
              precision={0}
              valueStyle={{ color: "#52c41a" }}
              prefix={<DollarOutlined />}
              suffix="ກີບ"
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder={["ວັນທີ່ເລີ່ມຕົ້ນ", "ວັນທີ່ສິ້ນສຸດ"]}
              onChange={handleDateRangeChange}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="ເລືອກພະນັກງານ"
              allowClear
              showSearch
              loading={userLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              value={params.userId}
              onChange={(value) =>
                setParams({ ...params, userId: value, page: 1 })
              }
              options={userOptions} // ✅ ใช้ memoized options
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="ເລືອກປະເພດ"
              allowClear
              value={params.type}
              onChange={(value) =>
                setParams({ ...params, type: value, page: 1 })
              }
              options={[
                { label: "ທັງໝົດ", value: undefined },
                { label: "ຄ່າບໍລິການ", value: "service" },
                { label: "ຄ່າແນະນຳ", value: "recommend" },
              ]}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button icon={<SearchOutlined />} type="primary">
                ຄົ້ນຫາ
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                ລ້າງ
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card
        title="ລາຍການຄ່າບໍລິການ"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            disabled={serviceLoading || userLoading}
          >
            ເພີ່ມໃໝ່
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={chargesData?.data?.datas || []}
          rowKey="_id"
          loading={isLoading}
          scroll={{ x: 1300 }}
          pagination={{
            current: params.page,
            pageSize: params.limit,
            total: chargesData?.data?.pagination?.total || 0,
            showTotal: (total) => `ທັງໝົດ ${total} ລາຍການ`,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
              setParams({ ...params, page, limit: pageSize });
            },
          }}
        />
      </Card>

      {/* Form Modal */}
      <ServiceChargeForm
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        loading={createMutation.isLoading || updateMutation.isLoading}
        initialValues={editingRecord}
        users={users}
        services={services}
      />
    </div>
  );
}

export default ServiceChargePage;